import { CABINET_PATHS, CABINET_PHOTO_SOURCE } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  combineLatest,
  firstValueFrom,
  map,
  startWith,
  Subject,
} from 'rxjs';
import { EmployeeAdminService } from '@nebalkon-workspace/admin-api';
import { PhotoUploadService } from '@nebalkon-workspace/media-api';
import {
  WarehouseResponse,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import { CabinetButtonComponent } from '@nebalkon-workspace/cabinet-client';
import {
  NotInput,
  NotPhoneInput,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { getErrorText, mobilePhoneValidator } from '@nebalkon-workspace/utils';

const FIELD_LIMITS = {
  NAME_MAX: 30,
  POSITION_MAX: 50,
  ACTS_ON_BASIS_MAX: 500,
  MIN_LENGTH: 2,
} as const;

@Component({
  selector: 'lib-staff-employee-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    NotInput,
    NotPhoneInput,
    CabinetButtonComponent,
  ],
  templateUrl: './staff-employee-form.component.html',
  styleUrl: './staff-employee-form.component.scss',
})
export class StaffEmployeeFormComponent implements OnInit {
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #photoSource = inject(CABINET_PHOTO_SOURCE, { optional: true });
  readonly #fb = inject(FormBuilder);
  readonly #employeeService = inject(EmployeeAdminService);
  readonly #warehouseService = inject(WarehouseService);
  readonly #photoUpload = inject(PhotoUploadService);
  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #translate = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);

  protected readonly rolesDropdown = viewChild<NotInput>('rolesDropdown');

  protected readonly form: FormGroup;
  protected readonly isSubmitting = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly isWarehousesLoading = signal(false);
  protected readonly unfocusedPhoneInput = new Subject<boolean>();
  protected readonly phoneControlErrorText;
  protected readonly employeeId = signal<string | null>(null);
  protected readonly isEditMode = computed(() => !!this.employeeId());
  protected readonly initialFormValue = signal<Record<string, unknown> | null>(
    null,
  );
  protected readonly isFormChanged = signal(true);
  protected readonly selectedRoles = signal<
    Array<{ id: string | number; label: string }>
  >([]);
  protected readonly warehouses = signal<WarehouseResponse[]>([]);

  protected readonly photoFile = signal<File | null>(null);
  protected readonly photoPreview = signal<string | null>(null);
  protected readonly existingPhotoUrl = signal<string | null>(null);

  protected readonly displayPhotoUrl = computed(() => {
    const preview = this.photoPreview();
    if (preview) {
      return preview;
    }
    return this.existingPhotoUrl();
  });

  protected readonly isSubmitDisabled = computed(() => {
    if (this.isSubmitting() || this.isLoading()) {
      return true;
    }
    if (
      this.isEditMode() &&
      !this.isFormChanged() &&
      !this.photoFile()
    ) {
      return true;
    }
    return false;
  });

  protected readonly availableRoles = computed(() => [
    {
      id: 'admin',
      label: this.#translate.instant('admin.employees.roles.admin'),
    },
    {
      id: 'manager',
      label: this.#translate.instant('admin.employees.roles.manager'),
    },
    {
      id: 'warehouse_worker',
      label: this.#translate.instant('admin.employees.roles.warehouse_worker'),
    },
    {
      id: 'mover',
      label: this.#translate.instant('admin.employees.roles.mover'),
    },
    {
      id: 'driver',
      label: this.#translate.instant('admin.employees.roles.driver'),
    },
  ]);
  protected readonly warehouseOptions = computed(() =>
    this.warehouses().map((warehouse) => ({
      id: warehouse.id,
      label: warehouse.name,
    })),
  );

  constructor() {
    this.form = this.#fb.group({
      phone: ['', [Validators.required, mobilePhoneValidator()]],
      lastName: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      firstName: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      patronymic: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.NAME_MAX),
        ],
      ],
      position: [
        '',
        [
          Validators.required,
          Validators.minLength(FIELD_LIMITS.MIN_LENGTH),
          Validators.maxLength(FIELD_LIMITS.POSITION_MAX),
        ],
      ],
      roles: [[], [Validators.required, this.#minArrayLength(1)]],
      email: ['', [Validators.email]],
      warehouseId: [''],
      telegramLink: [''],
      actsOnBasis: ['', [Validators.maxLength(FIELD_LIMITS.ACTS_ON_BASIS_MAX)]],
    });

    this.phoneControlErrorText = toSignal(
      combineLatest([
        this.form.controls['phone'].valueChanges.pipe(startWith('')),
        this.unfocusedPhoneInput,
      ]).pipe(
        map(() =>
          getErrorText(this.form.controls['phone'], {
            required: this.#translate.instant(
              'admin.employees.create.errors.required',
            ),
            invalidPhone: this.#translate.instant(
              'admin.employees.create.errors.invalidOperator',
            ),
            landlinePhone: this.#translate.instant(
              'admin.employees.create.errors.landlinePhone',
            ),
            invalidOperatorCode: this.#translate.instant(
              'admin.employees.create.errors.invalidOperator',
            ),
          }),
        ),
      ),
      { initialValue: '' },
    );
  }

  #minArrayLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value || !Array.isArray(value)) {
        return null;
      }
      return value.length >= min
        ? null
        : { minArrayLength: { min, actual: value.length } };
    };
  }

  public ngOnInit(): void {
    this.#loadWarehouses();
    const id = this.#route.snapshot.paramMap.get('id');
    if (id) {
      this.employeeId.set(id);
      void this.#loadEmployeeData(id);
    } else {
      this.employeeId.set(null);
      this.existingPhotoUrl.set(null);
      this.photoFile.set(null);
      this.photoPreview.set(null);
      this.initialFormValue.set(null);
      this.isFormChanged.set(true);
    }

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        if (this.isEditMode() && this.initialFormValue()) {
          const current = this.form.value;
          const initial = this.initialFormValue();
          this.isFormChanged.set(
            JSON.stringify(current) !== JSON.stringify(initial),
          );
        }
      });
  }

  protected getFieldError(fieldName: string): string {
    const control = this.form.get(fieldName);
    if (!control || !control.touched || !control.errors) {
      return '';
    }

    const errors = control.errors;

    if (errors['required']) {
      return 'admin.employees.create.errors.required';
    }
    if (errors['minlength']) {
      return 'admin.employees.create.errors.minLength';
    }
    if (errors['maxlength']) {
      const max = errors['maxlength'].requiredLength;
      return this.#translate.instant(
        'admin.employees.create.errors.maxLength',
        { max },
      );
    }
    if (errors['minArrayLength']) {
      return 'admin.employees.create.errors.minRoles';
    }
    if (errors['email']) {
      return 'admin.employees.create.errors.invalidEmail';
    }

    return '';
  }

  protected async onPickPhoto(): Promise<void> {
    if (this.isLoading() || !this.#photoSource) {
      if (!this.#photoSource) {
        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'error',
          text: [this.#translate.instant('admin.employees.photo.uploadError')],
        });
      }
      return;
    }

    const files = await this.#photoSource.pickPhotos({
      maxCount: 1,
      accept: 'image/jpeg,image/png,image/webp',
    });
    const file = files[0];
    if (!file) {
      return;
    }

    const preview = URL.createObjectURL(file);
    this.photoFile.set(file);
    this.photoPreview.set(preview);
    if (this.isEditMode()) {
      this.isFormChanged.set(true);
    }
  }

  protected clearPendingPhoto(): void {
    const preview = this.photoPreview();
    if (preview?.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }

    this.photoFile.set(null);
    this.photoPreview.set(null);
    if (this.isEditMode() && this.initialFormValue()) {
      const current = this.form.value;
      const initial = this.initialFormValue();
      this.isFormChanged.set(
        JSON.stringify(current) !== JSON.stringify(initial),
      );
    }
  }

  protected onRolesChange(
    selectedRoles: Array<{ id: string | number; label: string }>,
  ): void {
    const roleIds = selectedRoles.map((role) => role.id as string);
    this.form.patchValue({ roles: roleIds });
    this.form.get('roles')?.markAsTouched();
    this.selectedRoles.set(selectedRoles);
  }

  #loadWarehouses(): void {
    this.isWarehousesLoading.set(true);
    this.#warehouseService
      .getAdminWarehouses()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (response) => {
          this.warehouses.set(response.data ?? []);
          this.isWarehousesLoading.set(false);
        },
        error: () => {
          this.warehouses.set([]);
          this.isWarehousesLoading.set(false);
          this.#notification.addToQueue({
            id: Date.now().toString(),
            type: 'error',
            text: [this.#translate.instant('admin.employees.create.warehouseLoadError')],
          });
        },
      });
  }

  async #loadEmployeeData(id: string): Promise<void> {
    this.isLoading.set(true);
    this.photoFile.set(null);
    this.photoPreview.set(null);

    try {
      const employee = await firstValueFrom(
        this.#employeeService.getEmployee(id),
      );

      if (employee) {
        const u = employee.user;
        const ind = employee.individual;
        this.existingPhotoUrl.set(u.photo_url?.trim() || null);
        const formValue = {
          phone: u.phone,
          lastName: ind.last_name,
          firstName: ind.first_name,
          patronymic: ind.patronymic,
          position: employee.position || '',
          roles: u.roles || [],
          email: u.email || '',
          warehouseId: employee.warehouse_id || '',
          telegramLink: employee.telegram_link || '',
          actsOnBasis: employee.acts_on_basis || '',
        };

        this.form.patchValue(formValue);
        this.initialFormValue.set(structuredClone(formValue));
        this.isFormChanged.set(false);

        const selectedRoleObjects = (u.roles || [])
          .map((roleId: string) =>
            this.availableRoles().find((r) => r.id === roleId),
          )
          .filter(Boolean) as Array<{ id: string | number; label: string }>;

        this.selectedRoles.set(selectedRoleObjects);

        setTimeout(() => {
          const dropdown = this.rolesDropdown();
          if (dropdown) {
            dropdown.writeValue(selectedRoleObjects);
          }
        }, 0);

        this.form.get('phone')?.markAsTouched();
      }
    } catch (error) {
      console.error('Failed to load employee:', error);
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [this.#translate.instant('admin.employees.edit.loadError')],
      });
      await this.#router.navigate([this.#cabinetPaths.staff, 'employees']);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach((key) => {
        this.form.get(key)?.markAsTouched();
      });
      this.unfocusedPhoneInput.next(true);
      return;
    }

    this.isSubmitting.set(true);

    try {
      const formValue = this.form.value;
      const phone = (formValue.phone ?? '').trim();
      const warehouseId = formValue.warehouseId?.trim() || undefined;

      if (this.isEditMode()) {
        const empId = this.employeeId();
        if (!empId) {
          return;
        }

        await firstValueFrom(
          this.#employeeService.updateEmployee(empId, {
            phone,
            first_name: formValue.firstName.trim(),
            last_name: formValue.lastName.trim(),
            patronymic: formValue.patronymic.trim(),
            position: formValue.position?.trim() || undefined,
            roles: formValue.roles,
            email: formValue.email?.trim() || undefined,
            warehouse_id: warehouseId,
            telegram_link: formValue.telegramLink?.trim() || undefined,
            acts_on_basis: formValue.actsOnBasis?.trim() || undefined,
          }),
        );

        const pendingPhoto = this.photoFile();
        if (pendingPhoto) {
          try {
            const confirm = await firstValueFrom(
              this.#photoUpload.uploadPhoto('employee', empId, pendingPhoto),
            );
            this.existingPhotoUrl.set(confirm.url);
            this.clearPendingPhoto();
          } catch (uploadErr) {
            console.error('Employee photo upload failed', uploadErr);
            this.#notification.addToQueue({
              id: Date.now().toString(),
              type: 'error',
              text: [
                this.#translate.instant('admin.employees.photo.uploadError'),
              ],
            });
            return;
          }
        }

        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.employees.edit.success')],
        });

        await this.#router.navigate([
          this.#cabinetPaths.staff,
          'employees',
          empId,
        ]);
      } else {
        const created = await firstValueFrom(
          this.#employeeService.createEmployee({
            phone,
            first_name: formValue.firstName.trim(),
            last_name: formValue.lastName.trim(),
            patronymic: formValue.patronymic.trim(),
            position: formValue.position.trim(),
            roles: formValue.roles,
            email: formValue.email?.trim() || undefined,
            warehouse_id: warehouseId,
            telegram_link: formValue.telegramLink?.trim() || undefined,
            acts_on_basis: formValue.actsOnBasis?.trim() || undefined,
          }),
        );

        const pendingPhoto = this.photoFile();
        if (pendingPhoto && created?.user?.id) {
          try {
            await firstValueFrom(
              this.#photoUpload.uploadPhoto(
                'employee',
                created.user.id,
                pendingPhoto,
              ),
            );
            this.clearPendingPhoto();
          } catch (uploadErr) {
            console.error('Employee photo upload failed', uploadErr);
            this.#notification.addToQueue({
              id: Date.now().toString(),
              type: 'error',
              text: [
                this.#translate.instant('admin.employees.photo.uploadError'),
              ],
            });
            return;
          }
        }

        this.#notification.addToQueue({
          id: Date.now().toString(),
          type: 'success',
          text: [this.#translate.instant('admin.employees.create.success')],
        });

        await this.#router.navigate([this.#cabinetPaths.staff, 'employees']);
      }
    } catch (error) {
      console.error(
        `Failed to ${this.isEditMode() ? 'update' : 'create'} employee:`,
        error,
      );
      this.#notification.addToQueue({
        id: Date.now().toString(),
        type: 'error',
        text: [
          this.#translate.instant(
            this.isEditMode()
              ? 'admin.employees.edit.error'
              : 'admin.employees.create.error',
          ),
        ],
      });
    } finally {
      this.isSubmitting.set(false);
    }
  }
}
